/**
 * AdminChartOfAccountsTemplates — panel ITE „Wzorce planu kont".
 *
 * Centralny katalog wzorców planu kont zarządzany przez administratorów ITE (mirror
 * `AdminGlobalDictionaries`, ale bez cyklu statusów draft/published/withdrawn — `isActive`
 * wystarcza, patrz `docs/ADR/0081-plan-kont-wzorce-globalne-copy-on-adopt.md`). Model
 * **COPY-ON-ADOPT**: tenant adoptuje wzorzec do planu kont swojej firmy (kopia jednorazowa,
 * `POST /api/chart-of-accounts/adopt-template`) — edycja wzorca w tym panelu NIGDY nie
 * propaguje się do już-adoptowanych kont.
 *
 * Master-detail: lewa kolumna — lista wzorców (nazwa, opis, liczba pozycji, aktywność),
 * prawa kolumna — drzewo pozycji wybranego wzorca (mirror tree UX `ChartOfAccountsTab`
 * tenanta — rekurencyjne wiersze z wcięciem + rozwiń/zwiń per węzeł).
 *
 * Mutacje gatowane `hasPermission('chart_of_accounts_templates.manage')` — bez tego
 * uprawnienia user widzi tylko listę + pozycje (GET wymaga `.view`).
 *
 * Endpoint: /api/admin/chart-of-accounts-templates.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ChartTemplateFormModal } from '@/components/chart-of-accounts-templates/ChartTemplateFormModal';
import {
  ChartTemplateItemFormModal,
  type ChartTemplateItemParentOption,
} from '@/components/chart-of-accounts-templates/ChartTemplateItemFormModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { InfoHint } from '@/components/InfoHint';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  type AdminChartTemplate,
  type AdminChartTemplateItem,
  type ChartTemplateFormValues,
  type ChartTemplateItemFormValues,
  emptyChartTemplateFormValues,
  emptyChartTemplateItemFormValues,
  useAddChartTemplateItem,
  useChartTemplateDetail,
  useChartTemplates,
  useCreateChartTemplate,
  useDeleteChartTemplate,
  useDeleteChartTemplateItem,
  useRestoreChartTemplate,
  useUpdateChartTemplate,
  useUpdateChartTemplateItem,
} from '@/lib/chart-of-accounts-templates/api';

const MANAGE_PERMISSION = 'chart_of_accounts_templates.manage';
const VIEW_PERMISSION = 'chart_of_accounts_templates.view';

type ApiErrorCode = 'NAME_TAKEN' | 'ACCOUNT_NUMBER_TAKEN' | 'PARENT_CYCLE' | 'VALIDATION_FAILED';

function extractErrorCode(err: unknown): ApiErrorCode | null {
  if (!isAxiosError(err)) return null;
  return (err.response?.data?.error as ApiErrorCode | undefined) ?? null;
}

function toFormValues(tpl: AdminChartTemplate): ChartTemplateFormValues {
  return { name: tpl.name, description: tpl.description ?? '', isActive: tpl.isActive };
}

function toItemFormValues(item: AdminChartTemplateItem): ChartTemplateItemFormValues {
  return {
    parentId: item.parentId ?? '',
    accountNumber: item.accountNumber,
    name: item.name,
    accountType: item.accountType,
    isSettlement: item.isSettlement,
    normalSide: item.normalSide ?? '',
    isPostable: item.isPostable,
  };
}

// ─── Drzewo pozycji (spłaszczanie + wiersz rekurencyjny, mirror ChartOfAccountsTab) ────

interface ItemTreeNode extends AdminChartTemplateItem {
  children: ItemTreeNode[];
}

function buildItemTree(items: AdminChartTemplateItem[]): ItemTreeNode[] {
  const nodes = new Map<string, ItemTreeNode>();
  for (const item of items) nodes.set(item.id, { ...item, children: [] });

  const roots: ItemTreeNode[] = [];
  for (const item of items) {
    const node = nodes.get(item.id)!;
    const parent = item.parentId ? nodes.get(item.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

interface FlatItemRow extends ItemTreeNode {
  depth: number;
}

function flattenItemTree(nodes: ItemTreeNode[], depth = 0): FlatItemRow[] {
  const out: FlatItemRow[] = [];
  for (const node of nodes) {
    out.push({ ...node, depth });
    out.push(...flattenItemTree(node.children, depth + 1));
  }
  return out;
}

function collectDescendantIds(all: FlatItemRow[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const row of all) {
      if (row.parentId && ids.has(row.parentId) && !ids.has(row.id)) {
        ids.add(row.id);
        changed = true;
      }
    }
  }
  return ids;
}

export function AdminChartOfAccountsTemplates() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const toast = useToast();

  const canView = hasPermission(VIEW_PERMISSION);
  const canManage = hasPermission(MANAGE_PERMISSION);

  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminChartTemplate | null>(null);
  const [formServerErrors, setFormServerErrors] = useState<Record<string, string>>({});
  const [pendingRestore, setPendingRestore] = useState<AdminChartTemplate | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminChartTemplate | null>(null);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const autoExpandedForRef = useRef<string | null>(null);
  const [createItemParentId, setCreateItemParentId] = useState<string | null>(null);
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminChartTemplateItem | null>(null);
  const [itemServerErrors, setItemServerErrors] = useState<Record<string, string>>({});
  const [pendingDeleteItem, setPendingDeleteItem] = useState<AdminChartTemplateItem | null>(null);

  const listQ = useChartTemplates(includeDeleted);
  const detailQ = useChartTemplateDetail(selectedId);

  const createMutation = useCreateChartTemplate();
  const updateMutation = useUpdateChartTemplate();
  const deleteMutation = useDeleteChartTemplate();
  const restoreMutation = useRestoreChartTemplate();

  const addItemMutation = useAddChartTemplateItem(selectedId ?? '');
  const updateItemMutation = useUpdateChartTemplateItem(selectedId ?? '');
  const deleteItemMutation = useDeleteChartTemplateItem(selectedId ?? '');

  const templates = listQ.data ?? [];
  const detail = detailQ.data ?? null;
  const items = useMemo(() => detail?.items ?? [], [detail]);
  const itemTree = useMemo(() => buildItemTree(items), [items]);
  const flatItems = useMemo(() => flattenItemTree(itemTree), [itemTree]);

  useEffect(() => {
    if (detail && autoExpandedForRef.current !== detail.id) {
      setExpandedIds(new Set(flatItems.map((r) => r.id)));
      autoExpandedForRef.current = detail.id;
    }
  }, [detail, flatItems]);

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function parentOptionsFor(excludeId: string | null): ChartTemplateItemParentOption[] {
    const excluded = excludeId ? collectDescendantIds(flatItems, excludeId) : new Set<string>();
    return flatItems
      .filter((row) => !excluded.has(row.id))
      .map((row) => ({
        id: row.id,
        accountNumber: row.accountNumber,
        name: row.name,
        depth: row.depth,
      }));
  }

  function handleCreateSubmit(values: ChartTemplateFormValues) {
    setFormServerErrors({});
    createMutation.mutate(values, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.created') });
        setCreateOpen(false);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        if (code === 'NAME_TAKEN') {
          setFormServerErrors({ name: t('admin.chartTemplates.toast.nameTaken') });
          return;
        }
        toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.createError') });
      },
    });
  }

  function handleUpdateSubmit(values: ChartTemplateFormValues) {
    if (!editing) return;
    setFormServerErrors({});
    updateMutation.mutate(
      { id: editing.id, values, original: editing },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.updated') });
          setEditing(null);
        },
        onError: (err) => {
          const code = extractErrorCode(err);
          if (code === 'NAME_TAKEN') {
            setFormServerErrors({ name: t('admin.chartTemplates.toast.nameTaken') });
            return;
          }
          toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.updateError') });
        },
      },
    );
  }

  function handleDelete() {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.deleted') });
        if (selectedId === pendingDelete.id) setSelectedId(null);
        setPendingDelete(null);
      },
      onError: () => {
        toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.deleteError') });
        setPendingDelete(null);
      },
    });
  }

  function handleRestore() {
    if (!pendingRestore) return;
    restoreMutation.mutate(pendingRestore.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.restored') });
        setPendingRestore(null);
      },
      onError: () => {
        toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.restoreError') });
        setPendingRestore(null);
      },
    });
  }

  function openCreateItem(parentId: string | null) {
    setCreateItemParentId(parentId);
    setItemServerErrors({});
    setItemFormOpen(true);
  }

  function handleAddItemSubmit(values: ChartTemplateItemFormValues) {
    setItemServerErrors({});
    addItemMutation.mutate(values, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.itemCreated') });
        setItemFormOpen(false);
      },
      onError: (err) => {
        const code = extractErrorCode(err);
        if (code === 'ACCOUNT_NUMBER_TAKEN') {
          setItemServerErrors({ accountNumber: t('admin.chartTemplates.toast.itemNumberTaken') });
          return;
        }
        toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.itemCreateError') });
      },
    });
  }

  function handleUpdateItemSubmit(values: ChartTemplateItemFormValues) {
    if (!editingItem) return;
    setItemServerErrors({});
    updateItemMutation.mutate(
      { itemId: editingItem.id, values, original: editingItem },
      {
        onSuccess: () => {
          toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.itemUpdated') });
          setEditingItem(null);
        },
        onError: (err) => {
          const code = extractErrorCode(err);
          if (code === 'ACCOUNT_NUMBER_TAKEN') {
            setItemServerErrors({ accountNumber: t('admin.chartTemplates.toast.itemNumberTaken') });
            return;
          }
          if (code === 'PARENT_CYCLE') {
            toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.parentCycle') });
            return;
          }
          toast.show({ variant: 'error', title: t('admin.chartTemplates.toast.itemUpdateError') });
        },
      },
    );
  }

  function handleDeleteItem() {
    if (!pendingDeleteItem) return;
    deleteItemMutation.mutate(pendingDeleteItem.id, {
      onSuccess: () => {
        toast.show({ variant: 'success', title: t('admin.chartTemplates.toast.itemDeleted') });
        setPendingDeleteItem(null);
      },
      onError: (err) => {
        const code = isAxiosError(err) ? (err.response?.data as { error?: string })?.error : null;
        toast.show({
          variant: code === 'TEMPLATE_ITEM_HAS_CHILDREN' ? 'warning' : 'error',
          title:
            code === 'TEMPLATE_ITEM_HAS_CHILDREN'
              ? t('admin.chartTemplates.toast.itemHasChildren')
              : t('admin.chartTemplates.toast.itemDeleteError'),
        });
        setPendingDeleteItem(null);
      },
    });
  }

  if (!canView) {
    return (
      <Layout>
        <section className="px-6 py-8">
          <div
            role="alert"
            className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
          >
            {t('admin.chartTemplates.noPermission')}
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="px-6 py-8">
        <header className="mb-6 flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('admin.chartTemplates.title')}</h1>
            <InfoHint>{t('admin.chartTemplates.subtitle')}</InfoHint>
          </div>
          {canManage && (
            <Button
              variant="primary"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setFormServerErrors({});
                setCreateOpen(true);
              }}
              data-testid="chart-template-new-btn"
            >
              {t('admin.chartTemplates.newTemplate')}
            </Button>
          )}
        </header>

        <div className="mb-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              data-testid="chart-template-include-deleted"
            />
            {t('admin.chartTemplates.showDeleted')}
          </label>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          {/* Lewa kolumna — lista wzorców */}
          <div>
            {listQ.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : listQ.isError ? (
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
              >
                {t('admin.chartTemplates.loadError')}
              </div>
            ) : templates.length === 0 ? (
              <Card padding="md">
                <p className="text-sm text-text-tertiary">{t('admin.chartTemplates.empty')}</p>
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border-primary" data-testid="chart-template-list">
                  {templates.map((tpl) => (
                    <li key={tpl.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedId(tpl.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedId(tpl.id);
                          }
                        }}
                        data-testid={`chart-template-row-${tpl.id}`}
                        className={
                          'flex w-full items-center justify-between gap-2 px-4 py-3 text-left transition ' +
                          (selectedId === tpl.id ? 'bg-bg-secondary' : 'hover:bg-bg-tertiary/60')
                        }
                      >
                        <div className="min-w-0">
                          <span className="truncate font-medium text-text-primary">{tpl.name}</span>
                          <div className="mt-1 flex items-center gap-2">
                            <Badge color={tpl.isActive ? 'green' : 'gray'}>
                              {tpl.isActive
                                ? t('admin.chartTemplates.status.active')
                                : t('admin.chartTemplates.status.inactive')}
                            </Badge>
                            <span className="text-xs text-text-muted">
                              {t('admin.chartTemplates.columns.itemCount', {
                                count: tpl.itemCount ?? 0,
                              })}
                            </span>
                          </div>
                        </div>
                        {canManage && (
                          <div
                            className="flex shrink-0 items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                setFormServerErrors({});
                                setEditing(tpl);
                              }}
                              aria-label={t('admin.chartTemplates.actions.edit')}
                              className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                              data-testid={`chart-template-edit-${tpl.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            {tpl.gcrecord == null ? (
                              <button
                                type="button"
                                onClick={() => setPendingDelete(tpl)}
                                aria-label={t('admin.chartTemplates.actions.delete')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-error-500/10 hover:text-error-500"
                                data-testid={`chart-template-delete-${tpl.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setPendingRestore(tpl)}
                                aria-label={t('admin.chartTemplates.actions.restore')}
                                className="rounded p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-text-primary"
                                data-testid={`chart-template-restore-${tpl.id}`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Prawa kolumna — drzewo pozycji wybranego wzorca */}
          <div>
            {!selectedId ? (
              <Card padding="md">
                <p className="text-sm text-text-tertiary">
                  {t('admin.chartTemplates.selectPrompt')}
                </p>
              </Card>
            ) : detailQ.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : detailQ.isError || !detail ? (
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
              >
                {t('admin.chartTemplates.detailLoadError')}
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-text-primary">
                    {t('admin.chartTemplates.itemsTitle', { name: detail.name })}
                  </h2>
                  {canManage && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Plus className="h-3.5 w-3.5" />}
                      onClick={() => openCreateItem(null)}
                      data-testid="chart-template-item-new-btn"
                    >
                      {t('admin.chartTemplates.newItem')}
                    </Button>
                  )}
                </div>

                {items.length === 0 ? (
                  <Card padding="md">
                    <p className="text-sm text-text-tertiary">
                      {t('admin.chartTemplates.itemsEmpty')}
                    </p>
                  </Card>
                ) : (
                  <Card padding="none">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="chart-template-items-table">
                        <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                          <tr>
                            <th className="px-3 py-2 text-left">
                              {t('tenant.chartOfAccounts.list.colNumber')}
                            </th>
                            <th className="px-3 py-2 text-left">
                              {t('tenant.chartOfAccounts.list.colName')}
                            </th>
                            <th className="px-3 py-2 text-left">
                              {t('tenant.chartOfAccounts.list.colType')}
                            </th>
                            <th className="px-3 py-2 text-left">
                              {t('tenant.chartOfAccounts.list.colNormalSide')}
                            </th>
                            <th className="px-3 py-2 text-left">
                              {t('tenant.chartOfAccounts.list.colFlags')}
                            </th>
                            {canManage && <th className="px-3 py-2" />}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-primary">
                          {itemTree.map((node) => (
                            <ItemTreeRow
                              key={node.id}
                              node={node}
                              depth={0}
                              expandedIds={expandedIds}
                              onToggleExpand={toggleExpand}
                              canManage={canManage}
                              onAddChild={openCreateItem}
                              onEdit={(item) => {
                                setItemServerErrors({});
                                setEditingItem(item);
                              }}
                              onDelete={setPendingDeleteItem}
                              t={t}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <ChartTemplateFormModal
        open={createOpen || editing !== null}
        mode={editing ? 'edit' : 'create'}
        initialValues={editing ? toFormValues(editing) : emptyChartTemplateFormValues()}
        saving={createMutation.isPending || updateMutation.isPending}
        serverErrors={formServerErrors}
        onSubmit={(values) => {
          if (editing) handleUpdateSubmit(values);
          else handleCreateSubmit(values);
        }}
        onClose={() => {
          setCreateOpen(false);
          setEditing(null);
          setFormServerErrors({});
        }}
      />

      <ChartTemplateItemFormModal
        open={itemFormOpen || editingItem !== null}
        mode={editingItem ? 'edit' : 'create'}
        initialValues={
          editingItem
            ? toItemFormValues(editingItem)
            : emptyChartTemplateItemFormValues(createItemParentId)
        }
        saving={addItemMutation.isPending || updateItemMutation.isPending}
        serverErrors={itemServerErrors}
        parentOptions={parentOptionsFor(editingItem?.id ?? null)}
        onSubmit={(values) => {
          if (editingItem) handleUpdateItemSubmit(values);
          else handleAddItemSubmit(values);
        }}
        onClose={() => {
          setItemFormOpen(false);
          setEditingItem(null);
          setItemServerErrors({});
        }}
      />

      <ConfirmDialog
        open={pendingRestore !== null}
        title={t('admin.chartTemplates.confirmRestore.title')}
        message={
          pendingRestore
            ? t('admin.chartTemplates.confirmRestore.message', { name: pendingRestore.name })
            : ''
        }
        confirmLabel={t('common.restore')}
        variant="primary"
        onConfirm={handleRestore}
        onClose={() => setPendingRestore(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('admin.chartTemplates.confirmDelete.title')}
        message={
          pendingDelete
            ? t('admin.chartTemplates.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={pendingDeleteItem !== null}
        title={t('admin.chartTemplates.confirmDeleteItem.title')}
        message={
          pendingDeleteItem
            ? t('admin.chartTemplates.confirmDeleteItem.message', {
                number: pendingDeleteItem.accountNumber,
                name: pendingDeleteItem.name,
              })
            : ''
        }
        variant="danger"
        onConfirm={handleDeleteItem}
        onClose={() => setPendingDeleteItem(null)}
      />
    </Layout>
  );
}

// ─── Wiersz drzewa pozycji (rekurencyjny) ───────────────────────────────────

interface ItemTreeRowProps {
  node: ItemTreeNode;
  depth: number;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  canManage: boolean;
  onAddChild: (parentId: string) => void;
  onEdit: (item: AdminChartTemplateItem) => void;
  onDelete: (item: AdminChartTemplateItem) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function ItemTreeRow({
  node,
  depth,
  expandedIds,
  onToggleExpand,
  canManage,
  onAddChild,
  onEdit,
  onDelete,
  t,
}: ItemTreeRowProps) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);

  return (
    <>
      <tr data-testid={`chart-template-item-row-${node.id}`}>
        <td className="py-2 pr-2" style={{ paddingLeft: `${depth * 20 + 12}px` }}>
          <div className="flex items-center gap-1.5">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => onToggleExpand(node.id)}
                aria-label={
                  isExpanded
                    ? t('tenant.chartOfAccounts.list.collapseNode')
                    : t('tenant.chartOfAccounts.list.expandNode')
                }
                data-testid={`chart-template-item-toggle-${node.id}`}
                className="rounded p-0.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary"
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="inline-block h-3.5 w-3.5" />
            )}
            <span className="font-mono text-xs text-text-secondary">{node.accountNumber}</span>
          </div>
        </td>
        <td className="px-3 py-2 font-medium text-text-primary">{node.name}</td>
        <td className="px-3 py-2">
          <Badge color="accent">
            {t(`tenant.chartOfAccounts.accountTypes.${node.accountType}`)}
          </Badge>
        </td>
        <td className="px-3 py-2 text-text-secondary">
          {node.normalSide ? t(`tenant.chartOfAccounts.normalSides.${node.normalSide}`) : '—'}
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-1">
            {node.isSettlement && (
              <Badge color="blue">{t('tenant.chartOfAccounts.badges.settlement')}</Badge>
            )}
            {!node.isPostable && (
              <Badge color="gray">{t('tenant.chartOfAccounts.badges.notPostable')}</Badge>
            )}
          </div>
        </td>
        {canManage && (
          <td className="px-3 py-2">
            <div className="flex items-center justify-end gap-1">
              <button
                type="button"
                onClick={() => onAddChild(node.id)}
                aria-label={t('tenant.chartOfAccounts.list.addChild')}
                data-testid={`chart-template-item-add-child-${node.id}`}
                className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onEdit(node)}
                aria-label={t('common.edit')}
                data-testid={`chart-template-item-edit-${node.id}`}
                className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onDelete(node)}
                aria-label={t('common.delete')}
                data-testid={`chart-template-item-delete-${node.id}`}
                className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-danger-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </td>
        )}
      </tr>
      {isExpanded &&
        node.children.map((child) => (
          <ItemTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            canManage={canManage}
            onAddChild={onAddChild}
            onEdit={onEdit}
            onDelete={onDelete}
            t={t}
          />
        ))}
    </>
  );
}
